import { DateTime } from 'luxon';
import { Allocation, ConflictsByDay } from '../Timeline.types';

export function calculateConflictsByDay(
    allocations: Allocation[],
    startDateTime: DateTime,
    durationInDays: number,
): ConflictsByDay {
    const conflictsByDay: ConflictsByDay = new Map();

    // Group allocations by location
    const locationIdAllocationsMap = new Map<string, Allocation[]>();
    for (const allocation of allocations) {
        const group = locationIdAllocationsMap.get(allocation.locationId);
        if (group !== undefined) {
            group.push(allocation);
        } else {
            locationIdAllocationsMap.set(allocation.locationId, [allocation]);
        }
    }

    // For each location with 2+ allocations, use a sweep line to find days where 2+ allocations overlap.
    // Day indices are relative to the timeline start and clamped to [0, durationInDays).
    for (const locationAllocations of locationIdAllocationsMap.values()) {
        // Conflicts in this location are only possible if there are at least 2 allocations
        if (locationAllocations.length < 2) {
            continue;
        }

        // Idea: We only store the days where something changes (= an allocation starts or ends) and store the change on this day
        const boundaries: { day: number; delta: 1 | -1 }[] = [];
        for (const allocation of locationAllocations) {
            const startDay = Math.max(0, Math.round(allocation.startDateTime.diff(startDateTime, 'days').days));
            const endDay = Math.min(
                durationInDays,
                Math.round(allocation.endDateTime.diff(startDateTime, 'days').days),
            );

            boundaries.push({ day: startDay, delta: 1 });
            boundaries.push({ day: endDay, delta: -1 });
        }

        boundaries.sort((a, b) => a.day - b.day);

        let count = 0;
        let i = 0;
        while (i < boundaries.length) {
            const day = boundaries[i].day;

            while (i < boundaries.length && boundaries[i].day === day) {
                count += boundaries[i].delta;
                i++;
            }

            if (count >= 2) {
                const nextDay = i < boundaries.length ? boundaries[i].day : durationInDays;
                for (let d = day; d < nextDay; d++) {
                    const isoDate = startDateTime.plus({ days: d }).toISODate();
                    if (!isoDate) {
                        throw new Error('Could not convert DateTime to ISO date string');
                    }
                    conflictsByDay.set(isoDate, 'Unresolved-Conflict');
                }
            }
        }
    }

    return conflictsByDay;
}
